import store from "../store";
import {
  getFirestore,
  doc,
  getDoc,
  getDocFromServer,
  collection,
  getDocs,
} from "firebase/firestore";
import { app } from "../firebase/config";
import { fromUnixTime, format } from 'date-fns';
import { cleanPhoneNumber, removeLeadingZero, months } from '@/composables/Helpers';
import { formatCalendarEventTitle } from '@/composables/Booking/bookingWarrantyHelpers';

// Initialize Firestore
const db = getFirestore(app);

/**
 * @param {string} month - e.g. "January"
 * @param {string} year - e.g. "2026"
 * @param {{ touchStore?: boolean, ownerUid?: string }} [options] - default touchStore true (clears listBookings). Set false for analytics-only reads.
 */
export const getBookings = async (month, year, options = {}) => {
  const readDoc = options.serverOnly ? getDocFromServer : getDoc;
  const touchStore = options.touchStore !== false;
  const uid =
    options.ownerUid || store.state.accountOwnerId || store.state.currentUser?.uid;
  if (!uid) {
    return { bookings: {}, otherBookings: {} };
  }
  const userRef = doc(db, "users", uid);
  let bookings = {};
  let otherBookings = {};
  if (touchStore) {
    store.state.listBookings = [];
  }
  // We don't clear customers data here anymore to prevent losing customer data
  // when fetching bookings for a specific month/year

  try {
    // Legacy code that should never be removed
    const customerRef = doc(collection(userRef, "bookings"), `${month}${year}`);
    const mainBookingRef = doc(
      collection(userRef, "bookings", "mainBookings", year),
      month
    );
    const otherBookingsRef = doc(
      collection(userRef, "bookings", "otherBookings", year),
      month
    );

    // Fetch legacy bookings
    const customerSnap = await readDoc(customerRef);
    if (customerSnap.exists()) {
      Object.assign(bookings, customerSnap.data());
    }

    // Fetch updated bookings
    const mainBookingSnap = await readDoc(mainBookingRef);
    if (mainBookingSnap.exists()) {
      const tempBookingObject = mainBookingSnap.data();
      // Collect all day entries by bookingId first; then decide single vs multi-day by count of days.
      // This ensures the same bookingId on multiple days (e.g. Feb 02–06) is always merged even when
      // each day's document doesn't have is_multi_day or timeslots set.
      const byBookingId = new Map();
      
      for (const [day, dayBookings] of Object.entries(tempBookingObject)) {
        for (const [bookingId, bookingData] of Object.entries(dayBookings)) {
          if (!bookingData || !bookingData.booking || !bookingData.booking.installDetails || bookingData.booking.installDetails.price === undefined) {
            continue;
          }
          try {
            const isBookingVersionV2 = bookingData.booking.installDetails.bookingVersion === "v2";
            if (!isBookingVersionV2) {
              const startTime = bookingData.booking.installDetails.startTime;
              const endTime = bookingData.booking.installDetails.endTime;
              if (startTime == null || endTime == null || !Number.isFinite(Number(startTime)) || !Number.isFinite(Number(endTime))) {
                continue;
              }
              const startDate = fromUnixTime(startTime);
              const endDate = fromUnixTime(endTime);
              if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
                continue;
              }
              bookingData.booking.installDetails.selectedDay = format(startDate, 'dd');
              bookingData.booking.installDetails.selectedMonth = format(startDate, 'MMMM');
              bookingData.booking.installDetails.selectedYear = format(startDate, 'yyyy');
              bookingData.booking.installDetails.startTime = format(startDate, 'HH:mm');
              bookingData.booking.installDetails.endTime = format(endDate, 'HH:mm');
              bookingData.booking.installDetails.bookingVersion = "v2";
            }

            const copy = {
              ...bookingData,
              booking: {
                ...bookingData.booking,
                sendReview: bookingData.booking.sendReview === true ? true : false,
                customerDetails: { ...bookingData.booking.customerDetails },
                installDetails: { ...bookingData.booking.installDetails },
                vehicleDetails: { ...bookingData.booking.vehicleDetails }
              }
            };

            if (!byBookingId.has(bookingId)) {
              byBookingId.set(bookingId, []);
            }
            byBookingId.get(bookingId).push({ day, bookingData: copy });
          } catch (_) {
            continue;
          }
        }
      }
      
      // Single-day: one entry per bookingId. Multi-day: multiple day entries → merge with _multiDayEntries.
      // Dedupe by numeric day so the same booking under "4" and "04" (day-key mismatch) counts as one.
      for (const [bookingId, dayEntries] of byBookingId.entries()) {
        const seenNumDays = new Set();
        const uniqueByDay = dayEntries.filter((e) => {
          const numDay = parseInt(String(e.day).replace(/^0+/, '') || 0, 10);
          if (seenNumDays.has(numDay)) return false;
          seenNumDays.add(numDay);
          return true;
        });
        if (uniqueByDay.length > 1) {
          uniqueByDay.sort((a, b) => {
            const aIndex = a.bookingData.day_index ?? a.bookingData.booking?.installDetails?.day_index ?? parseInt(String(a.day), 10);
            const bIndex = b.bookingData.day_index ?? b.bookingData.booking?.installDetails?.day_index ?? parseInt(String(b.day), 10);
            return aIndex - bIndex;
          });
          const primaryEntry = uniqueByDay[0].bookingData;
          bookings[`${bookingId}`] = primaryEntry;
          bookings[`${bookingId}`]._multiDayEntries = uniqueByDay.map(e => e.bookingData);
        } else {
          bookings[`${bookingId}`] = uniqueByDay[0].bookingData;
        }
      }
    }

    // Fetch other bookings
    const otherBookingsSnap = await readDoc(otherBookingsRef);
    if (otherBookingsSnap.exists()) {
      const tempOtherBookingObject = otherBookingsSnap.data();
      for (const [day, dayBookings] of Object.entries(tempOtherBookingObject)) {
        // Iterate through all bookings for this day
        for (const [bookingId, bookingData] of Object.entries(dayBookings)) {
          if (bookingData && bookingData.serviceDetails && bookingData.serviceDetails.serviceName) {
            // Check if it's a v2 booking
            const isBookingVersionV2 = bookingData.serviceDetails.bookingVersion === "v2";
            
            if (!isBookingVersionV2) {
              // Convert Unix timestamp to selectedDay, selectedMonth, selectedYear
              const startDate = fromUnixTime(bookingData.serviceDetails.startTime);
              const endDate = fromUnixTime(bookingData.serviceDetails.endTime);
              
              bookingData.serviceDetails.selectedDay = format(startDate, 'dd');
              bookingData.serviceDetails.selectedMonth = format(startDate, 'MMMM');
              bookingData.serviceDetails.selectedYear = format(startDate, 'yyyy');
              
              // Convert startTime and endTime to HH:mm format
              bookingData.serviceDetails.startTime = format(startDate, 'HH:mm');
              bookingData.serviceDetails.endTime = format(endDate, 'HH:mm');

              // Update version to v2
              bookingData.serviceDetails.bookingVersion = "v2";
            }
            
            otherBookings[`${bookingId}`] = mergeOtherCalendarRecords(otherBookings[`${bookingId}`], bookingData);
          } else {
            // console.warn(`Corrupted or empty **OTHER** booking found for day ${day} of ${month} ${year}, booking ID ${bookingId} in otherBookings`);
          }
        }
      }
    }
  } catch (error) {
    console.log("<<== ERROR IN GET BOOKINGS ==>>", error);
    if (options.serverOnly) throw error;
  }

  return { bookings, otherBookings };
};

/** Month/year pairs for notification scans (install month may differ from today). */
export function getNotificationBookingMonthRange({
  lookBehind = 1,
  lookAhead = 12,
} = {}) {
  const pairs = [];
  const now = new Date();
  for (let offset = -lookBehind; offset <= lookAhead; offset += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    pairs.push({
      month: months[d.getMonth()],
      year: d.getFullYear().toString(),
    });
  }
  return pairs;
}

/**
 * Load and merge main + other bookings across multiple calendar months (parallel reads).
 * @param {{ month: string, year: string }[]} monthYearPairs
 * @param {{ touchStore?: boolean, ownerUid?: string }} [options]
 */
export async function getBookingsAcrossMonths(monthYearPairs, options = {}) {
  if (!monthYearPairs?.length) {
    return { bookings: {}, otherBookings: {} };
  }
  const chunks = await Promise.all(
    monthYearPairs.map(({ month, year }) => getBookings(month, year, options)),
  );
  const bookings = {};
  const otherBookings = {};
  for (const data of chunks) {
    Object.assign(bookings, data.bookings || {});
    for (const [id, booking] of Object.entries(data.otherBookings || {})) {
      otherBookings[id] = mergeOtherCalendarRecords(otherBookings[id], booking);
    }
  }
  return { bookings, otherBookings };
}

// Custom services use one record per day without the main booking's full
// timeslots array. Preserve every loaded day instead of keeping only the last.
export function mergeOtherCalendarRecords(previous, next) {
  if (!previous) return next;
  const entries = [...(previous._multiDayEntries || [previous]), ...(next._multiDayEntries || [next])];
  const dateKey = entry => entry.slot_detail?.date || `${entry.serviceDetails.selectedYear}-${String(months.indexOf(entry.serviceDetails.selectedMonth) + 1).padStart(2, '0')}-${String(entry.serviceDetails.selectedDay).padStart(2, '0')}`;
  const unique = [...new Map(entries.map(entry => [dateKey(entry), entry])).entries()].sort(([a], [b]) => a.localeCompare(b));
  const primary = unique[0][1];
  const slots = unique.map(([date, entry]) => ({date, start_time: entry.serviceDetails.startTime, end_time: entry.serviceDetails.endTime}));
  const total = Math.max(unique.length, ...unique.map(([, entry]) => Number(entry.total_days || 1)));
  return {...primary, _multiDayEntries: unique.map(([, entry]) => entry), total_days: total, is_multi_day: total > 1,
    serviceDetails: {...primary.serviceDetails, timeslots: slots, isMultiDay: total > 1}};
}

/**
 * Transform getBookings(month, year) main bookings into the calendar's per-day format.
 * Used by Calendar_UI_2.0 callBookings and store/modules/bookings fetchBookings so
 * store.state.fetchedBookings has the same shape after refresh (multi-day indicators work).
 * @param {Object} bookings - bookings object from getBookings (keyed by bookingId)
 * @param {string} selectedMonth - month name (e.g. "February")
 * @param {string} selectedYear - year (e.g. "2026")
 * @returns {Array} Array of per-day entries: { title, start, end, day, month, id, details, version, bookingStatus, price, isMultiDay?, dayIndex?, dontTrackBooking, cancelled }
 */
function calendarTitleForBooking(bookingData, installDetails) {
  const vehicle = bookingData.booking?.vehicleDetails || {};
  return formatCalendarEventTitle(installDetails, vehicle);
}

export function transformBookingsToCalendarFormat(bookings, selectedMonth, selectedYear) {
  const result = [];
  for (const booking of Object.entries(bookings)) {
    const bookingData = booking[1];
    const installDetails = bookingData.booking?.installDetails;
    if (!installDetails) continue;

    const hasMultiDayEntries = bookingData._multiDayEntries && Array.isArray(bookingData._multiDayEntries) && bookingData._multiDayEntries.length > 1;
    const isMultiDay = bookingData.is_multi_day === true ||
      installDetails.isMultiDay === true ||
      (installDetails.timeslots &&
        Array.isArray(installDetails.timeslots) &&
        installDetails.timeslots.length > 1) ||
      hasMultiDayEntries;

    const timeslotsOk = installDetails.timeslots && Array.isArray(installDetails.timeslots) && installDetails.timeslots.length > 1;

    if (isMultiDay && timeslotsOk) {
      for (const timeslot of installDetails.timeslots) {
        if (!timeslot.date) continue;
        const [slotYear, slotMonth, slotDay] = timeslot.date.split('-');
        const slotMonthIndex = parseInt(slotMonth, 10) - 1;
        const slotMonthName = months[slotMonthIndex];
        if (slotYear === selectedYear.toString() && slotMonthName === selectedMonth) {
          result.push({
            title: calendarTitleForBooking(bookingData, installDetails),
            start: timeslot.start_time,
            end: timeslot.end_time,
            day: removeLeadingZero(slotDay),
            month: slotMonthIndex,
            year: Number(slotYear), segmentDate: timeslot.date,
            id: booking[0],
            details: booking,
            version: installDetails.bookingVersion || "v2",
            bookingStatus: installDetails.status,
            price: installDetails.price,
            isWarranty: !!installDetails.isWarranty,
            dontTrackBooking: bookingData.dontTrackBooking || false,
            cancelled: bookingData.cancelled || false,
            isMultiDay: true,
            dayIndex: timeslot.day_index
          });
        }
      }
    } else if (isMultiDay && bookingData._multiDayEntries && Array.isArray(bookingData._multiDayEntries) && bookingData._multiDayEntries.length > 0) {
      // Primary entry may not have full timeslots (e.g. when loaded from Firestore per-day docs).
      // Build one calendar entry per day from _multiDayEntries so month view and multi-day bar show correctly.
      for (const entry of bookingData._multiDayEntries) {
        const entryInst = entry?.booking?.installDetails;
        if (!entryInst) continue;
        let dateStr = entryInst.slot_detail?.date;
        if (!dateStr && entryInst.selectedDay != null && entryInst.selectedMonth && entryInst.selectedYear) {
          const monthNum = months.findIndex((m) => m === entryInst.selectedMonth) + 1;
          const d = String(entryInst.selectedDay).padStart(2, '0');
          const m = String(monthNum).padStart(2, '0');
          dateStr = `${entryInst.selectedYear}-${m}-${d}`;
        }
        if (!dateStr) continue;
        const [slotYear, slotMonth, slotDay] = dateStr.split('-');
        const slotMonthIndex = parseInt(slotMonth, 10) - 1;
        const slotMonthName = months[slotMonthIndex];
        if (slotYear !== selectedYear.toString() || slotMonthName !== selectedMonth) continue;
        const startTime = entryInst.slot_detail?.start_time ?? entryInst.startTime ?? '';
        const endTime = entryInst.slot_detail?.end_time ?? entryInst.endTime ?? '';
        result.push({
          title: calendarTitleForBooking(bookingData, entryInst),
          start: startTime,
          end: endTime,
          day: removeLeadingZero(slotDay),
          month: slotMonthIndex,
          year: Number(slotYear), segmentDate: dateStr,
          id: booking[0],
          details: booking,
          version: installDetails.bookingVersion || "v2",
          bookingStatus: installDetails.status,
          price: installDetails.price,
          isWarranty: !!entryInst.isWarranty,
          dontTrackBooking: bookingData.dontTrackBooking || false,
          cancelled: bookingData.cancelled || false,
          isMultiDay: true,
          dayIndex: entry.day_index ?? entry.dayIndex
        });
      }
    } else {
      result.push({
        title: calendarTitleForBooking(bookingData, installDetails),
        start: installDetails.startTime,
        end: installDetails.endTime,
        day: removeLeadingZero(String(installDetails.selectedDay ?? '')),
        month: months.findIndex((item) => item === installDetails.selectedMonth),
        id: booking[0],
        details: booking,
        version: installDetails.bookingVersion,
        bookingStatus: installDetails.status,
        price: installDetails.price,
        isWarranty: !!installDetails.isWarranty,
        dontTrackBooking: bookingData.dontTrackBooking || false,
        cancelled: bookingData.cancelled || false
      });
    }
  }
  return result;
}

export const getAllBookings = async () => {
  let bookings = {};
  const bookingsCollectionRef = collection(
    db,
    "users",
    store.state.accountOwnerId || store.state.currentUser.uid,
    "customers"
  );

  try {
    const querySnapshot = await getDocs(bookingsCollectionRef);
    querySnapshot.forEach((customerDoc) => {
      const data = customerDoc.data();
      const canonicalId = cleanPhoneNumber(
        data?.customer?.phoneNumber || customerDoc.id
      ) || customerDoc.id;
      const previous = bookings[canonicalId] || {};
      bookings[canonicalId] = {
        ...previous,
        ...data,
        customer: {
          ...(previous.customer || {}),
          ...(data.customer || {}),
          phoneNumber: canonicalId,
        },
        bookings: {
          ...(previous.bookings || {}),
          ...(data.bookings || {}),
        },
        leads: {
          ...(previous.leads || {}),
          ...(data.leads || {}),
        },
      };
    });
    
    // This is a complete collection read. Replace the cache so stale legacy
    // phone-key entries cannot survive the canonical merge above.
    store.state.customers = bookings;
    
  } catch (error) {
    console.error("<<== ERROR IN GET ALL BOOKINGS ==>>", error);
  }
};

export const checkNoShowStatus = async (phoneNumber) => {
  const ownerUid = store.state.accountOwnerId || store.state.currentUser.uid;
  const canonicalPhone = cleanPhoneNumber(String(phoneNumber || ""));
  const candidateIds = [...new Set([
    canonicalPhone,
    String(phoneNumber || ""),
    canonicalPhone ? `1${canonicalPhone}` : "",
    canonicalPhone ? `+1${canonicalPhone}` : "",
  ].filter(Boolean))];

  try {
    const snapshots = await Promise.all(candidateIds.map((customerId) =>
      getDoc(doc(db, "users", ownerUid, "customers", customerId))
    ));
    const docSnapshot = snapshots.find((snapshot) => snapshot.exists());

    if (docSnapshot) {
      const customerData = docSnapshot.data();
      
      // Merge with existing customers data instead of overwriting
      if (!store.state.customers) {
        store.state.customers = {};
      }
      store.state.customers[docSnapshot.id] = customerData;
    } else {
      console.log("No matching customer record found for:", phoneNumber);
    }
  } catch (error) {
    ("<<== ERROR IN GETTING CUSTOMER ==>>", error);
  }
};
